import { useEffect, useMemo, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import { LogSearchBar } from '../logs/LogSearchBar';
import { filterLogLinesWithContext, highlightMatches } from '../logs/logSearch';

interface WorkflowLogsViewerProps {
  isOpen: boolean;
  onClose: () => void;
  runId: string;
  workflowName: string;
  status: string;
}

export function WorkflowLogsViewer({ isOpen, onClose, runId, workflowName, status }: WorkflowLogsViewerProps) {
  const logContainerRef = useRef<HTMLDivElement>(null);
  const [logs, setLogs] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
  const [isFollowEnabled, setIsFollowEnabled] = useState(true);
  const pinnedRef = useRef(true);
  const eventSourceRef = useRef<EventSource | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [ctxBefore, setCtxBefore] = useState(0);
  const [ctxAfter, setCtxAfter] = useState(0);
  const [selectedPod, setSelectedPod] = useState<string>('__all__');
  const [liveSteps, setLiveSteps] = useState<Array<{ podName: string; stepName?: string; phase?: string; startedAt?: string }>>([]);
  const isRunning = status === 'Running' || status === 'Pending';

  // Auto-follow while streaming unless user scrolled up
  useEffect(() => {
    if (String(searchQuery).trim()) return; // searching: don't auto-follow
    if (!isStreaming) return;
    if (!isFollowEnabled) return;
    if (!pinnedRef.current) return;
    const el = logContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [logs, isStreaming, searchQuery, isFollowEnabled]);

  useEffect(() => {
    if (!isOpen) return;
    setLogs('');
    setIsPinnedToBottom(true);
    setIsFollowEnabled(true);
    setSearchQuery('');
    setCtxBefore(0);
    setCtxAfter(0);
    setSelectedPod('__all__');
    setLiveSteps([]);
    loadLogs();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsStreaming(false);
    };
  }, [isOpen, runId]);

  // While running, poll step/pod meta so the selector updates even before the backend starts streaming that pod.
  useEffect(() => {
    if (!isOpen) return;
    if (!isRunning) return;

    let cancelled = false;
    const tick = async () => {
      try {
        const res = await apiClient.getWorkflowRunLogSteps(runId);
        const steps = (res?.steps || []) as Array<{ podName: string; stepName?: string; phase?: string; startedAt?: string }>;
        if (cancelled) return;
        setLiveSteps(steps.filter((s) => s?.podName));
      } catch {
        // best-effort: fall back to parsing log text markers
        if (cancelled) return;
        setLiveSteps([]);
      }
    };

    tick();
    const id = window.setInterval(tick, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [isOpen, isRunning, runId]);

  const loadLogs = async () => {
    if (isRunning) {
      // Stream logs via SSE
      setIsStreaming(true);
      setLogs('Streaming logs...\n');

      try {
        const eventSource = apiClient.streamWorkflowLogs(runId);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);
            if (data.done) {
              eventSource.close();
              eventSourceRef.current = null;
              setIsStreaming(false);
              setLogs((prev) => prev + '\n--- Stream ended ---\n');
              return;
            }
            if (data.log) {
              setLogs((prev) => prev + data.log);
            }
            if (data.error) {
              eventSource.close();
              eventSourceRef.current = null;
              setIsStreaming(false);
              setLogs((prev) => prev + `\nError: ${data.error}\n`);
            }
          } catch (err) {
            console.error('Failed to parse SSE data:', err);
          }
        };

        eventSource.onerror = (err) => {
          console.error('SSE error:', err);
          eventSource.close();
          eventSourceRef.current = null;
          setIsStreaming(false);
          setLogs((prev) => prev + '\n--- Connection error, attempting to fetch historical logs ---\n');
          fetchHistoricalLogs();
        };
      } catch (error: any) {
        console.error('Failed to stream logs:', error);
        setLogs((prev) => prev + `Error: ${error.message}\n`);
        setIsStreaming(false);
        fetchHistoricalLogs();
      }
    } else {
      // Fetch historical logs
      fetchHistoricalLogs();
    }
  };

  const fetchHistoricalLogs = async () => {
    try {
      setLogs((prev) => (prev ? prev + '\n' : '') + 'Loading logs...\n');
      const response = await apiClient.getWorkflowRunLogs(runId);
      
      if (response.logs) {
        setLogs(response.logs);
      } else if (response.completed) {
        setLogs('No logs available.\n');
      } else {
        setLogs('Logs not yet available.\n');
      }
    } catch (error: any) {
      console.error('Failed to fetch logs:', error);
      setLogs(`Error: ${error.message}\n`);
    }
  };

  const downloadLogs = () => {
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workflowName}-${runId}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const allLines = useMemo(() => logs.split('\n'), [logs]);
  const podsFromLogs = useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    const re = /^--- pod\/([^\s]+)\s/;
    for (const line of allLines) {
      const m = line.match(re);
      const podName = m?.[1];
      if (!podName) continue;
      if (seen.has(podName)) continue;
      seen.add(podName);
      out.push(podName);
    }
    return out;
  }, [allLines]);

  const liveStepLabelByPod = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of liveSteps) {
      if (s?.podName && s?.stepName) m.set(s.podName, s.stepName);
    }
    return m;
  }, [liveSteps]);

  const availablePods = useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const s of liveSteps) {
      const podName = s?.podName;
      if (!podName || seen.has(podName)) continue;
      seen.add(podName);
      out.push(podName);
    }
    for (const podName of podsFromLogs) {
      if (seen.has(podName)) continue;
      seen.add(podName);
      out.push(podName);
    }
    return out;
  }, [liveSteps, podsFromLogs]);

  const deriveStepLabelFromPod = (podName: string) => {
    // Keep behavior close to the old implementation to avoid changing "history logs" UX.
    let label = podName;
    const prefix = `${workflowName}-`;
    if (podName.startsWith(prefix)) {
      const rest = podName.slice(prefix.length);
      const restNoHash = rest.replace(/-\d+$/, '');
      label = restNoHash || rest;
    }
    return label;
  };

  const stepFilteredLines = useMemo(() => {
    if (selectedPod === '__all__') return allLines;
    const out: string[] = [];
    const re = /^--- pod\/([^\s]+)\s/;
    let inSection = false;
    for (const line of allLines) {
      const m = line.match(re);
      if (m && m[1]) {
        const podName = m[1];
        inSection = podName === selectedPod;
      }
      if (inSection) out.push(line);
    }
    return out;
  }, [allLines, selectedPod]);

  const filtered = useMemo(
    () => filterLogLinesWithContext(stepFilteredLines, searchQuery, ctxBefore, ctxAfter),
    [stepFilteredLines, searchQuery, ctxBefore, ctxAfter],
  );
  const hasSearch = String(searchQuery).trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent maxWidth="4xl" className="flex flex-col h-[90vh] overflow-hidden w-full md:w-[80vw] md:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle>Workflow Logs: {workflowName}</DialogTitle>
        </DialogHeader>

        <DialogBody className="flex-1 p-0 min-h-0 overflow-hidden">
          <div className="flex flex-col min-h-0 h-full">
            <LogSearchBar
              query={searchQuery}
              setQuery={setSearchQuery}
              before={ctxBefore}
              setBefore={setCtxBefore}
              after={ctxAfter}
              setAfter={setCtxAfter}
              matchCount={filtered.matchCount}
              totalLines={allLines.length}
            />

            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {isStreaming ? 'Live stream (SSE)' : 'Logs'}
              </div>
              <div className="flex items-center gap-2">
                {availablePods.length > 0 && (
                  <select
                    className="h-9 px-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
                    value={selectedPod}
                    onChange={(e) => setSelectedPod(e.target.value)}
                  >
                    <option value="__all__">All steps</option>
                    {availablePods.map((podName) => (
                      <option key={podName} value={podName}>
                        {liveStepLabelByPod.get(podName) || deriveStepLabelFromPod(podName)}
                      </option>
                    ))}
                  </select>
                )}
                {isStreaming && !hasSearch && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (isFollowEnabled) {
                        setIsFollowEnabled(false);
                        return;
                      }
                      setIsFollowEnabled(true);
                      const el = logContainerRef.current;
                      if (!el) return;
                      el.scrollTop = el.scrollHeight;
                      pinnedRef.current = true;
                      setIsPinnedToBottom(true);
                    }}
                  >
                    {isFollowEnabled ? 'Unfollow' : 'Follow'}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setLogs('')}>
                  Clear
                </Button>
                <Button size="sm" variant="outline" onClick={downloadLogs} disabled={!logs}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>

            <div className="flex-1 min-h-0 relative">
              <div
                ref={logContainerRef}
                className="h-full overflow-y-auto p-4 bg-gray-900 text-green-400 font-mono text-sm whitespace-pre-wrap"
                onScroll={() => {
                  const el = logContainerRef.current;
                  if (!el) return;
                  const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
                  pinnedRef.current = nearBottom;
                  setIsPinnedToBottom(nearBottom);
                }}
              >
                {filtered.lines.length === 0 ? (
                  <div className="text-gray-500">{hasSearch ? 'No matches' : 'No logs yet'}</div>
                ) : (
                  filtered.lines.map((l) => (
                    <div
                      key={`log-${l.idx}`}
                      className={l.isMatch ? 'bg-yellow-500/10 ring-1 ring-yellow-500/30 rounded px-1' : (hasSearch ? 'opacity-80' : '')}
                    >
                      {highlightMatches(l.text, searchQuery).map((p, i) =>
                        p.isHit ? (
                          <span key={i} className="bg-yellow-500/40 text-yellow-50 px-0.5 rounded">
                            {p.text}
                          </span>
                        ) : (
                          <span key={i}>{p.text}</span>
                        ),
                      )}
                    </div>
                  ))
                )}
              </div>

              {isStreaming && !hasSearch && !isPinnedToBottom && (
                <div className="absolute right-3 bottom-3">
                  <Button
                    size="sm"
                    onClick={() => {
                      const el = logContainerRef.current;
                      if (!el) return;
                      el.scrollTop = el.scrollHeight;
                      pinnedRef.current = true;
                      setIsPinnedToBottom(true);
                      setIsFollowEnabled(true);
                    }}
                  >
                    Jump to bottom
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

